import 'assets/js/components/my-combobox/my-combobox';

import { LitElement, PropertyValues, TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { cache } from 'lit/directives/cache.js';
import { live } from 'lit/directives/live.js';
import { join } from 'lit/directives/join.js';
import { ref } from 'lit/directives/ref.js';
import { consume } from '@lit/context';
import type WaNumberInput from '@awesome.me/webawesome/dist/components/number-input/number-input.js';
import _ from "lodash";

import { TniJsonDevice, TniJsonDeviceId, TniJsonDeviceLogicController, TniJsonPlug, TniJsonPlugId, TniJsonProgram, TniJsonProgramId, TniJsonUseConfigId, TniProduceLimitType, TniProgramControllerModifiers, TniSocketType, TniTraversalConsumptionPolicy } from 'raw/data-format13-spec';
import { dataContext } from "./data-context";
import type { MyCombobox } from 'assets/js/components/my-combobox/my-combobox';
import { DataCached } from './data-cached';
import { EditorConfig, editorConfigContext } from './editor-config';
import { WaIcon, WaTooltip } from '@awesome.me/webawesome/dist/ssr/all.js';


const IRRELEVANT_DEVICES = new Set([
	"units/gpu_servers/gpu_test_server.tscn",
	"units/servers/test_server.tscn",
	"units/routers/test_router.tscn",
]);

// [[PROGRAM_ID|MODIFIER, ..., WORKS_AS_AND_OP], DESCRIPTION]
const BAD_PROGRAM_COMBINATIONS: [string[], string|TemplateResult][] = [
	// Commented out until confirmed that these are bad combinations.
	[[TniProgramControllerModifiers.ALLOW_PACKET_SWITCHING, TniProgramControllerModifiers.ALLOW_PACKET_SWITCHING], "multiple packet switching programs is redundant and potentially disruptive"],
	[[TniProgramControllerModifiers.ALLOW_PACKET_ROUTING, TniProgramControllerModifiers.ALLOW_PACKET_ROUTING], "multiple packet routing programs is redundant"],
	[[TniProgramControllerModifiers.ALLOW_PACKET_SWITCHING, TniProgramControllerModifiers.ALLOW_PACKET_ROUTING], "switching and routing programs do not work well together"],
	[["vlanfirm", TniProgramControllerModifiers.ALLOW_PACKET_SWITCHING], "switching and routing programs do not work well together"],
	[["vlanfirm", TniProgramControllerModifiers.ALLOW_PACKET_ROUTING], "switching and routing programs do not work well together"],
];


function _getDeepDiff<T extends Record<string, any>>(object: T, base: Record<string, any>): Partial<T> {
	return _.transform(object, (result: any, value: any, key: string) => {
		if (!_.isEqual(value, base[key])) {
			result[key] = (_.isObject(value) && _.isObject(base[key]))
				? _getDeepDiff(value as Record<string, any>, base[key] as Record<string, any>)
				: value;
		}
	});
}

// Converts TNI BBCode into a html template.
function _renderBBCode(description: string): TemplateResult[] {
	const re = /(\n)|\[(\w+)=(.*?)\]|\[\/(\w+)\]/g;
	const stack: TemplateResult[][] = [[]];
	const tag_stack = [];
	let lastEndPos = 0;
	while (re.lastIndex < description.length) {
		const startPos = re.lastIndex;
		const match = re.exec(description);
		const endPos = re.lastIndex;
		if (!match) break;
		lastEndPos = endPos;
		const matchStartPos = endPos - match[0].length;
		if (startPos < matchStartPos) {
			stack[stack.length-1]!.push(html`${description.slice(startPos, matchStartPos)}`);
		}

		if (match[0] == "\n") {
			stack[stack.length-1]!.push(html`<br>`);
		} else if (match[2]) {
			const value = match[3];
			switch (match[2]) {
				case "color":
					stack.push([]);
					tag_stack.push([match[2], value]);
					break;
				default:
					break;
			}
		} else if (match[4]) {
			if (tag_stack.length <= 0 && stack.length >= 2) continue;
			const elems = stack.pop();
			const [tag, value] = tag_stack.pop()!;
			switch (tag) {
				case "color":
					stack[stack.length-1]!.push(html`<span style="color: ${value};">${elems}</span>`);
					break;
				default:
					break;
			}
		}
	}
	if (lastEndPos < description.length) {
		stack[stack.length-1]!.push(html`${description.slice(lastEndPos, description.length)}`);
	}
	return stack[0]!;
}

function _renderProgramSize(program: TniJsonProgram): string {
	return `${program.code_size}${program.data_size ? `+${program.data_size}=${program.code_size+program.data_size}` : ""}`;
}

// AI Generated.
interface SumCombination {
    sum: number;
    values: number[];
}
function getSumCombinations(numbers: number[], maxLength: number): SumCombination[] {
    const uniqueSums = new Map<number, number[]>();

    function generate(currentCombo: number[], currentSum: number): void {
        // Store the unique sum and the combination that made it
        if (!uniqueSums.has(currentSum)) {
            uniqueSums.set(currentSum, [...currentCombo]);
        }

        // Base case: stop if we reached the maximum combination length
        if (currentCombo.length === maxLength) {
            return;
        }

        // Recursive case: allow repetition by starting from index 0 every time
        for (let i = 0; i < numbers.length; i++) {
            currentCombo.push(numbers[i]!);
            generate(currentCombo, currentSum + numbers[i]!);
            currentCombo.pop(); // Backtrack
        }
    }

    generate([], 0);
    
    return Array.from(uniqueSums.entries()).map(([sum, values]) => ({
        sum,
        values
    }));
}

export function getTotalSanStorageDelta(ref_device: DeviceEditor|undefined = undefined): number {
	const devices = document.getElementById("devices_list")?.querySelectorAll("device-editor") ?? (new NodeList() as NodeListOf<DeviceEditor>);
	let san_storage = EditorConfig.delta_san;
	let encountered_ref_device = false;
	for (let i = 0; i < devices.length; i++) {
		const device_editor = devices[i]!;
		if (device_editor == ref_device)
			encountered_ref_device = true;

		if (device_editor.has_san_target || device_editor.has_san_initiator) {
			const {excess_sto} = device_editor.getHardwareResources(true);

			if (device_editor.has_san_target) {
				san_storage += Math.max(0, excess_sto);
			}

			if (!encountered_ref_device && device_editor.has_san_initiator) {
				san_storage += Math.min(0, excess_sto);
			}
		}
	}
	return san_storage;
}
window.getTotalSanStorageDelta = getTotalSanStorageDelta;


interface CustomDeviceExt {
	CustomData?: CustomDeviceData,
}
interface CustomDeviceData {
	peripherals?: Record<number, TniJsonPlugId>;
}

interface SataCombinationData {
	storage: number;
	values: number[];
	price: number;
	plug_ids: string[];
}

@customElement('device-editor')
export class DeviceEditor extends LitElement {
	// This value is used to make sure the element get's re-rendered when the config changes.
	@consume({ context: editorConfigContext, subscribe: true })
	@state()
	private _editorConfig!: typeof EditorConfig;

	@consume({ context: dataContext, subscribe: true })
	@state()
	private _data!: DataCached|null;

	get device_name() { return this._data && this.device_id ? this._data!.devices[this.device_id]?.product_name ?? null : null; }
	@property({ reflect: true })
	device_id: TniJsonDeviceId|null = null;

	// Temporary one-shot value for when a new device is selected via the combobox.
	custom_data: { sata_combination: SataCombinationData }|undefined = undefined;

	get device_data_original() {
		return this._data && this.device_id ? this._data.devices[this.device_id] ?? null : null;
	}
	get device_data_is_original() {
		// TODO: This is quite ugly...
		let device_data = this.device_data;
		if (device_data?.CustomData) {
			device_data = {...device_data};
			device_data.CustomData = {...device_data.CustomData};
			if (_.isEmpty(device_data.CustomData!.peripherals)) {
				delete device_data.CustomData!.peripherals;
			}
			if (_.isEmpty(device_data.CustomData)) {
				delete device_data.CustomData;
			}
		}
		return _.isEqual(this.device_data_original, device_data);
	}
	@state()
	device_data: (TniJsonDevice&CustomDeviceExt)|null = null;

	// Reset to `null` upon merge.
	device_data_partial: Partial<TniJsonDevice&CustomDeviceExt>|null = null;

	difficulty = {
		device_warranty_period_multiplier: 1,
	};

	private __dropdown_items_satas_templates: TemplateResult<1>[] = [];
	private __dropdown_items_satas_templates_data?: any;
	private __dropdown_items_satas_current_day: number = -1;
	get _dropdown_items_satas_templates(): TemplateResult<1>[] {
		if (this.__dropdown_items_satas_templates_data !== this._data || this.__dropdown_items_satas_current_day != EditorConfig.current_day) {
			this.__dropdown_items_satas_templates_data = this._data;
			this.__dropdown_items_satas_current_day = EditorConfig.current_day;
			this.__dropdown_items_satas_templates = [];
			if (this._data) {
				for (const plug_id in this._data.plugs_by_type[TniSocketType.SATA35_SLOT]) {
					const plug = this._data.plugs[plug_id]!;
					if (plug.PeripheralPlug && plug.RemovableStorageDevice) {
						const available = EditorConfig.current_day <= -1 || (this._data?.isAvailableToday(plug_id, EditorConfig.current_day) ?? true);
						this.__dropdown_items_satas_templates.push(html`
							<wa-dropdown-item value="${plug_id}" style="${available ? '' : 'text-decoration-line: line-through;'}">
								+${plug.RemovableStorageDevice.available_sto} <span class="my-combobox-ignore">${plug.PeripheralPlug.product_name}</span>
								<span slot="details">
									<span style="display: inline-block; min-width: 5ch; ${available ? '' : 'text-decoration-line: line-through;'}">
										$${plug.PeripheralPlug.price}
									</span>
								</span>
							</wa-dropdown-item>
						`)
					}
				}
			}
		}
		return this.__dropdown_items_satas_templates;
	}

	private _sata_options: number[] = [];
	private _sata_prices: Record<number,number> = [];
	private _sata_ids: Record<number,TniJsonPlugId> = [];
	private __recalc_satas() {
		this._sata_options = [];
		this._sata_prices = [];
		this._sata_ids = [];
		if (this._data) {
			for (const plug_id in this._data.plugs_by_type[TniSocketType.SATA35_SLOT]) {
				const plug: TniJsonPlug = this._data.plugs[plug_id]!;
				if (plug.PeripheralPlug && plug.RemovableStorageDevice) {
					const sata_sto = plug.RemovableStorageDevice.available_sto;
					if (this._sata_options.indexOf(sata_sto) == -1){
						this._sata_options.push(sata_sto);
						this._sata_prices[sata_sto] = plug.PeripheralPlug.price;
						this._sata_ids[sata_sto] = plug_id;
					}
				}
			}
		}
	}
	private _getSataCombinations(sata_port_count: number) {
		const sata_combinations = getSumCombinations(this._sata_options, sata_port_count)
			.map(combination => {return {
				storage: combination.sum,
				values: combination.values,
				price: _.sumBy(combination.values, sata_size => this._sata_prices[sata_size]!),
				plug_ids: combination.values.map(sata_size => this._sata_ids[sata_size]!),
			} as SataCombinationData});

		sata_combinations.sort((a, b) => {
			return a.price - b.price;
		});

		return sata_combinations;
	}
	private _getProgramsRequirements(programs?: TniJsonProgramId[]): [number, number, number] {
		let programs_cpu = 0;
		let programs_mem = 0;
		let programs_size = 0;
		if (this._data && programs) {
			programs.forEach((program_id) => {
				const program = this._data?.programs[program_id];
				if (program) {
					programs_cpu += program.cpu_load;
					programs_mem += program.stack_size;
					programs_size += program.code_size + program.data_size;
				}
			});
		}
		return [programs_cpu, programs_mem, programs_size];
	}

	public getHardwareResources(isFromSanTotal = false): { excess_cpu: number, excess_mem: number, excess_sto: number, excess_sto_max: number, satas_size: number, extra_price: number, san_usage: number } {
		const [programs_cpu, programs_mem, programs_size] = this._getProgramsRequirements(this.device_data?.logic_controller?.installed_programs);
		
		const satas_sto_max = this.device_data?.logic_controller
			? Math.max(...this._getSataCombinations(this._getSataPorts(this.device_data?.logic_controller).length).map(value => value.storage))
			: 0;

		let satas_size = 0;
		var extra_price = 0;
		if (this.device_data?.CustomData?.peripherals) {
			for (const plug_id of Object.values(this.device_data.CustomData.peripherals)) {
				const plug = this._data!.plugs[plug_id];
				if (plug && plug.RemovableStorageDevice) {
					satas_size += plug.RemovableStorageDevice.available_sto;
				}
				if (plug && plug.PeripheralPlug) {
					extra_price += plug.PeripheralPlug.price;
				}
			}
		}

		const san_sto = this.has_san_initiator && !isFromSanTotal ? getTotalSanStorageDelta(this) : 0;
		
		const excess_cpu = (this.device_data?.logic_controller?.installed_cpu ?? 0) - programs_cpu;
		const excess_mem = (this.device_data?.logic_controller?.installed_mem ?? 0) - programs_mem;
		let excess_sto = (this.device_data?.logic_controller?.installed_sto  ?? 0) + satas_size - programs_size;
		const excess_sto_max = (this.device_data?.logic_controller?.installed_sto ?? 0) + satas_sto_max - programs_size + san_sto;

		var san_usage = excess_sto < 0 ? Math.max(0, Math.min(-excess_sto, san_sto)) : 0;
		excess_sto += san_usage;

		return {
			excess_cpu,
			excess_mem,
			excess_sto,
			excess_sto_max,
			satas_size,
			extra_price,
			san_usage,
		};
	}

	public get has_san_target() {
		return (this.device_data?.logic_controller?.installed_programs ?? []).includes("graph_network_simulation/programs/early_access/storage_and_file_system/san_target.tscn");
	}
	public get has_san_initiator() {
		return (this.device_data?.logic_controller?.installed_programs ?? []).includes("graph_network_simulation/programs/early_access/storage_and_file_system/san_initiator.tscn");
	}
	static override styles = css`
		wa-number-input::part(stepper) {
			aspect-ratio: unset;
		}
		wa-number-input::part(start),
		wa-number-input::part(end) {
			--wa-form-control-padding-inline: 0.25em;
		}
		wa-number-input:not(:has([slot="start"]:not(:empty)))::part(start),
		wa-number-input:not(:has([slot="end"]:not(:empty)))::part(end) {
			display: none;
		}
		wa-number-input::part(input) {
			padding: 0;
			width: max-content;
		}
		wa-number-input {
			width: 100px;
			--wa-content-spacing: 0;
		}

		.not-available-id::part(input) {
			text-decoration-line: line-through;
		}

		.tiny-button {
			line-height: 0;
		}
		.tiny-button::part(base) {
			--wa-form-control-height: 21px;
			aspect-ratio: unset;
		}
		
		:host .input-changed::part(input) {
			color: var(--wa-color-yellow);
		}

		:host .flex-wrap-gap {
			display: flex;
			flex-wrap: wrap;
			gap: calc(var(--wa-content-spacing) / 2);
		}

		:host .flex-gap {
			display: flex;
			gap: calc(var(--wa-content-spacing) / 2);
		}

		:host .flex-wrap {
			display: flex;
			flex-wrap: wrap;
		}

		:host .some-text {
			text-align: center;
			margin: 0;
		}
		:host .some-title {
			font-weight: var(--wa-form-control-label-font-weight);
			text-align: center;
			margin: 0;
		}

		:host .div-programs {
			container-name: div-programs;
			container-type: inline-size;
		}
		.tbl-programs th,
		.tbl-programs td {
			padding-right: var(--wa-content-spacing);
			align-self: center;
		}
		.tbl-programs th:last-child,
		.tbl-programs td:last-child {
			padding-right: 0;
		}

		.tbl-programs {
			width: 100%;
			display: grid;
			grid-template-columns: auto min-content min-content min-content min-content; 
		}
		.tbl-programs tr {
			display: contents;
		}
		.tbl-programs th {
			width: auto;
			min-width: 16em;
			max-width: 100%;
		}

		@container div-programs (max-width: 590px) {
			.tbl-programs {
				display: block;
			}
			.tbl-programs tr {
				display: grid;
    			grid-template-columns: max-content max-content max-content 1fr;
				width: 100%;
			}
			.tbl-programs th {
				grid-column: 1 / -1; 
				font-weight: bold;
				width: 100%;
			}
			.tbl-programs td {
				width: auto; 
			}
			.tbl-programs td:last-child {
				margin-left: auto;
			}
		}
		
		:host .header-device {
			align-items: center;
		}
		.header-device-combobox {
			width: 24em;
			min-width: 12em;
			max-width: 100%;
			flex-grow: 1;
		}
		.header-buttons {
			margin-left: auto;
		}
		.header-device-img {
			max-width: 100%;
			align-self: flex-start;
			padding-top: 0.5em;
		}

		wa-tooltip {
			--wa-tooltip-background-color: var(--wa-color-surface-raised);
			--wa-tooltip-content-color: var(--wa-color-text-normal);
			--wa-tooltip-border-color: var(--wa-color-surface-border);
			
			/* Also match the small indicator arrow */
			--wa-tooltip-arrow-color: var(--wa-color-surface-raised);

			--max-width: 75vw;
		}

		@media (min-width: 550px) {
			.device-dropdown-item-img-br {
				display: none;
			}
		}
	`;

	override render() {
		const [programs_cpu, programs_mem, programs_size] = this._getProgramsRequirements(this.device_data?.logic_controller?.installed_programs);
		const excess_san = getTotalSanStorageDelta(this);

		let body: TemplateResult|Array<TemplateResult>;
		if (this.device_id) {
			const device_original = this.device_data_original;
			const device = this.device_data;
			if (!device_original) {
				body = html`ERROR: Invalid \`deivce_id\``;
			} else if (!device) {
				body = html`ERROR: No device data?`;
			} else {
				device.CustomData ??= {};
				const custom_data = device.CustomData;

				const ideal_listing = this._data!.getIdealListingToday(this.device_id, EditorConfig.current_day);
				let total_price = device.price;
				let warranty = device.base_warranty_days + (device.base_warranty_cycles * this._data!.BalanceCalc.GLOBAL_WARRANTY_PERIOD_DAYS_PER_CYCLE * this.difficulty.device_warranty_period_multiplier);
				if (ideal_listing) {
					warranty *= ideal_listing.merchant.DeviceMerchant.warranty_multiplier;
					warranty += ideal_listing.merchant.DeviceMerchant.warranty_add_constant;
					total_price *= ideal_listing.merchant.DeviceMerchant.price_multiplier;
					total_price += ideal_listing.merchant.DeviceMerchant.price_add_constant;
				}

				const parts = [];
				if (device.logic_controller && device_original.logic_controller) {
					const logic_controller_original = device_original.logic_controller;
					const logic_controller = device.logic_controller;

					const satas_templates = this._generateTemplatesForPeripherals(logic_controller, custom_data);

					const {excess_cpu, excess_mem, excess_sto, excess_sto_max, satas_size, extra_price, san_usage} = this.getHardwareResources();
					total_price += extra_price;

					parts.push(html`
						<div class="flex-wrap-gap">
							<div class="flex-gap">
								<wa-number-input
									label="CPU" min="0" max="9999" step="1" .value=${live(String(logic_controller.installed_cpu))}
									appearance="filled" size="m"
									class=${logic_controller.installed_cpu == logic_controller_original.installed_cpu ? "" : "input-changed"}
									@input=${(e: InputEvent) => {
										logic_controller.installed_cpu = Number.parseInt((e.target as WaNumberInput).value ?? "0");
										this.requestUpdate();
									}}
									@blur=${(e: InputEvent) => {
										const input = e.target as WaNumberInput;
										if (!input.value) {
											logic_controller.installed_cpu = logic_controller_original.installed_cpu;
											this.requestUpdate();
										}
									}}
								></wa-number-input>
								<wa-number-input
									label="Memory" min="0" max="9999" step="1" .value=${live(String(logic_controller.installed_mem))}
									appearance="filled" size="m"
									class=${logic_controller.installed_mem == logic_controller_original.installed_mem ? "" : "input-changed"}
									@input=${(e: InputEvent) => {
										logic_controller.installed_mem = Number.parseInt((e.target as WaNumberInput).value ?? "0");
										this.requestUpdate();
									}}
									@blur=${(e: InputEvent) => {
										const input = e.target as WaNumberInput;
										if (!input.value) {
											logic_controller.installed_mem = logic_controller_original.installed_mem;
											this.requestUpdate();
										}
									}}
								></wa-number-input>
								<wa-number-input
									label="Storage" min="0" max="9999" step="1" .value=${live(String(logic_controller.installed_sto + satas_size))}
									appearance="filled" size="m"
									class=${logic_controller.installed_sto == logic_controller_original.installed_sto ? "" : "input-changed"}
									@input=${(e: InputEvent) => {
										logic_controller.installed_sto = Number.parseInt((e.target as WaNumberInput).value ?? "0") - satas_size;
										this.requestUpdate();
									}}
									@blur=${(e: InputEvent) => {
										const input = e.target as WaNumberInput;
										if (!input.value) {
											logic_controller.installed_sto = logic_controller_original.installed_sto;
											this.requestUpdate();
										}
									}}
								></wa-number-input>
							</div>
							${satas_templates.length <= 0 ? "" : html`
								<div class="flex-wrap" style="align-self: center; flex-direction: column;">
									<p class="some-title">SATA's</p>
									<div class="flex-wrap">
										${satas_templates}
									</div>
								</div>
							`}
							<!-- <p class="some-text"><span class="some-title">Price</span><br>$${total_price}</p> -->
							<p class="some-text"><span class="some-title">Warranty</span><br>${warranty} days</p>
							<p class="some-text"><span class="some-title">Wattage</span><br>${device.power_controller?.charge_rate ?? "?"}w</p>
							<p class="some-text"><span class="some-title">Bandwidth</span><br>${logic_controller.installed_nbw}/tick</p>
							<p class="some-text"><span class="some-title">Mount Type</span><br>${device.mount_type}</p>
						</div>
						<wa-divider></wa-divider>
						<div class="div-programs">
							<div class="flex-wrap-gap" style="align-items: center;">
								<h2 style="margin: 0;">Programs</h2>
								<div class="flex-gap flex-swap-2-3" style="flex-grow: 1; margin: 0; text-align: center; align-items: center;">
									<p style="color: ${excess_cpu < 0 ? 'red' : ''}; margin: 0;">CPU: ${programs_cpu}</p>
									<p style="color: ${excess_mem < 0 ? 'red' : ''}; margin: 0;">MEM: ${programs_mem}</p>
									<p style="color: ${excess_sto < 0 ? 'red' : ''}; margin: 0;">Size: ${programs_size}</p>
									${this.has_san_initiator ? html`<p style="color: ${(excess_san < 0 || excess_sto < 0) ? 'red' : ''}; margin: 0;">SAN Use: ${san_usage}</p>` : ""}

									${logic_controller_original.installed_programs.length > 0 && !logic_controller_original.installed_programs.every(v => logic_controller.installed_programs.includes(v) ?? true) ? html`
										<p class="some-text" style="color: var(--wa-color-orange-90);"><small>Jailbreaker<br>Required</small></p>
									` : ""}
									${logic_controller.installed_programs.filter( ( s, i, arr ) => s.length > 0 && arr.indexOf( s, i + 1 ) != -1 ).length > 0 ? html`
										<p class="some-text" style="color: var(--wa-color-orange-90);"><small>vmconf<br>Required</small></p>
									` : ""}
									
									<div style="margin-left: auto;">
										<wa-button appearance="plain" size="l"
											@click=${() => {
												logic_controller.installed_programs.push("");
												this.requestUpdate();
											}}
										>
											<wa-icon name="plus" variant="solid" label="Reset"
												style="color: green;"
											></wa-icon>
										</wa-button>
										<wa-button appearance="plain" size="l"
											@click=${() => {
												logic_controller.installed_programs = _.cloneDeep(logic_controller_original.installed_programs);
												this.requestUpdate();
											}}
										>
											<wa-icon name="rotate-left" variant="solid" label="Reset"
												style="color: ${_.isEqual(logic_controller.installed_programs, logic_controller_original.installed_programs) ? "" : "var(--wa-color-orange)"};"
											></wa-icon>
										</wa-button>
									</div>
								</div>
							</div>
							<table class="tbl-programs">
								${this._generateTemplatesForPrograms(logic_controller.installed_programs, excess_cpu, excess_mem, excess_sto, excess_sto_max)}
							</table>
						</div>
						<wa-divider></wa-divider>
						<div>
							<div class="flex-gap" style="align-items: center;" class="wa-invert">
								<h2 style="margin: 0;">Use Stack</h2>
								<wa-icon id="my_device-use_stack-info" name="circle-question" variant="regular"></wa-icon>
								<wa-tooltip for="my_device-use_stack-info">
									<span style="color: var(--wa-color-green-95);">[PRODUCTION/LIMIT] PRODUCE_TYPES</span><br>
									<span style="color: var(--wa-color-green-95);">[0/+LIMIT] STORAGE_TYPES</span><br>
									<span style="color: var(--wa-color-red-80);">[-CONSUME] CONSTRAINTS&nbsp;<small><i>(CONSUME_POLICY)</i></small></span><br>
									<span style="color: var(--wa-color-red-80);"><small><i>(fragmented allowed)</i></small></span> - Can consumes from multiple sources. Partial consumption is wasted. <br>
									<span style="color: var(--wa-color-red-80);"><small><i>(all or nothing)</i></small></span> - Consumes from a single source only. <br>
									In all cases, if the required CONSUME amount is not obtained, nothing is produced. <br>
								</wa-tooltip>
							</div>
							<table style="margin-left: var(--wa-content-spacing); border-collapse: separate; border-spacing: 10px 0;">
								${this._generateTemplatesForUseStack(logic_controller.installed_programs, programs_mem, programs_size)}
							</table>
						</div>
					`);
				}
				body = parts;
			}
		} else {
			body = html`No device selected...`;
		}

		let devices_dropdown_templates: TemplateResult[] = [];
		if (this._data) {
			const dropdownItemTemplate = (device_id: TniJsonDeviceId, color: string, details: TemplateResult[], sata_combination: SataCombinationData|null = null) => {
				const device = this._data!.devices[device_id]!;
				const available = EditorConfig.current_day <= -1 || this._data?.isAvailableToday(device_id, EditorConfig.current_day);
				return html`
					<wa-dropdown-item value=${device_id} style="color: ${color}; text-decoration-line: ${available ? '' : 'line-through'};"
						.custom_data=${{ sata_combination }}
					>
						<span>${device.product_name}</span>
						<br class="device-dropdown-item-img-br">
						<img src="${window.SiteConfig!.baseUrl}/tni-docs/assets/devices/${device?.product_name.replaceAll(/[\.\/\\]/g, "")}.webp" style="height: 1em; max-width: 100%;" />
						<div slot="details" style="display: contents;">
							<div style="margin-inline-start: 0.5em !important; text-decoration-line: ${available ? '' : 'line-through'};">
								${details}
							</div>
						</div>
					</wa-dropdown-item>
				`;
			};
			if (this.device_data_is_original || (programs_cpu <= 0 && programs_mem <= 0 && programs_size <= 0)) {
				for (const device_id in this._data.devices) {
					if (device_id.startsWith("units/")) continue;  // Deprecated devices.

					const device = this._data.devices[device_id]!;
					if (!device.logic_controller) continue;
					
					const ideal_listing = this._data.getIdealListingToday(device_id, EditorConfig.current_day);
					const total_price = ideal_listing ? ideal_listing.listing.AdditionalData.price : device.price;
					const details = [
						html`<span>${device.logic_controller.installed_cpu} / ${device.logic_controller.installed_mem} / ${device.logic_controller.installed_sto}</span>`,
						html`&nbsp;&nbsp;`,
						html`<span style="display: inline-block; min-width: 5ch;">$${total_price}</span>`,
					];
					const sata_slot_count = this._getSataPorts(device.logic_controller).length;
					if (sata_slot_count > 0) {
						details.push(html`<div>
							SATA: ${sata_slot_count} slots
						</div>`);
					}
					devices_dropdown_templates.push(dropdownItemTemplate(
						device_id,
						IRRELEVANT_DEVICES.has(device_id) ? "var(--wa-color-gray)" : "",
						details
					));
				}
			} else {
				const sata_options: number[] = [];
				const sata_prices: Record<number,number> = [];
				for (const plug_id in this._data.plugs_by_type[TniSocketType.SATA35_SLOT]) {
					const plug = this._data.plugs[plug_id]!;
					if (plug.PeripheralPlug && plug.RemovableStorageDevice) {
						if (sata_options.indexOf(plug.RemovableStorageDevice.available_sto) == -1){
							sata_options.push(plug.RemovableStorageDevice.available_sto);
							sata_prices[plug.RemovableStorageDevice.available_sto] = plug.PeripheralPlug.price;
						}
					}
				}

				const device_templates_scored: [number, number, TemplateResult][] = [];
				for (const device_id in this._data.devices) {
					if (device_id.startsWith("units/")) continue;  // Deprecated devices.

					const device = this._data.devices[device_id]!;
					if (!device.logic_controller) continue;

					const excess_cpu = device.logic_controller.installed_cpu - programs_cpu;
					const excess_mem = device.logic_controller.installed_mem - programs_mem;
					let excess_sto = device.logic_controller.installed_sto - programs_size;
					let sata_combination: SataCombinationData|null = null;
					if (excess_sto < 0) {
						const sata_combinations = this._getSataCombinations(this._getSataPorts(device.logic_controller).length);
						for (let i = 0; i < sata_combinations.length; i++) {
							const combination = sata_combinations[i]!;
							if (combination.storage <= 0)
								continue;
							if (!sata_combination || excess_sto + combination.storage > excess_sto + sata_combination.storage) {
								sata_combination = combination;
								if (excess_sto + sata_combination.storage >= 0)
									break;
							}
						}
					}
					if (sata_combination)
						excess_sto = excess_sto + sata_combination.storage;

					const san_sto = this.has_san_initiator ? getTotalSanStorageDelta(this) : 0;
					let san_usage = excess_sto < 0 ? Math.max(0, Math.min(-excess_sto, san_sto)) : 0;
					excess_sto += san_usage;

					const any_lacking = excess_cpu < 0 || excess_mem < 0 || excess_sto < 0;
					const score = IRRELEVANT_DEVICES.has(device_id) ? -Infinity
						: any_lacking ? Math.min(0, excess_cpu) + Math.min(0, excess_mem) + Math.min(0, excess_sto)
						: excess_cpu + excess_mem + excess_sto;

					const numFormat = new Intl.NumberFormat(undefined, { signDisplay: "exceptZero" });
					const ideal_listing = this._data.getIdealListingToday(device_id, EditorConfig.current_day);
					const total_price = (ideal_listing ? ideal_listing.listing.AdditionalData.price : device.price) + (sata_combination?.price ?? 0);
					const details = [
						html`<span>${!Number.isFinite(score) ? "" : html`${numFormat.format(excess_cpu)} / ${numFormat.format(excess_mem)} / ${numFormat.format(excess_sto)}`}</span>`,
						html`&nbsp;&nbsp;`,
						html`<span style="display: inline-block; min-width: 5ch; text-decoration: inherit;">$${total_price}</span>`,
					];
					if (sata_combination) {
						details.push(html`<div>
							SATA: ${sata_combination.values.map(numFormat.format).join(",")}
						</div>`);
					}
					if (san_usage > 0) {
						details.push(html`<div>
							SAN: ${san_usage}
						</div>`);
					}

					const color =
						IRRELEVANT_DEVICES.has(device_id) ? "var(--wa-color-gray)"
						: any_lacking ? "var(--wa-color-red-80)"
						: san_usage > 0 ? "var(--wa-color-yellow-90)"
						: "";
					device_templates_scored.push([score, total_price, dropdownItemTemplate(device_id, color, details, sata_combination)])
				}
				devices_dropdown_templates = device_templates_scored.sort(([a_score, a_price, a_template], [b_score, b_price, b_template]) => {
					// Sorted as; zero -> near zero -> negative 0 -> negative near zero
					if (a_score == b_score) return a_price - b_price;
					if (a_score < 0 && b_score >= 0) return 1;
					if (b_score < 0 && a_score >= 0) return -1;
					if (a_score < 0 && b_score < 0) return b_score - a_score; 
					return a_price - b_price;
				}).map(([score, price, template]) => template);
			}
		}

		
		const available = EditorConfig.current_day <= -1 || !this.device_id || (this._data?.isAvailableToday(this.device_id, EditorConfig.current_day) ?? true);

		return html`
			<wa-card class="card-header">
				<div slot="header">
					<div class="header-device flex-wrap-gap">
						<my-combobox size="l" class="header-device-combobox ${available ? '' : 'not-available-id'}" value=${this.device_id ?? ""} input_value=${this.device_name ?? ""}
							@my-value-confirm=${(e: CustomEvent<MyCombobox>) => {
								this.device_id = e.detail.value ?? null;
								this.custom_data = e.detail.custom_data;
							}}
						>
							<wa-icon name="server" slot="start"></wa-icon>
							${devices_dropdown_templates}
						</my-combobox>
						<div class="header-buttons">
							<wa-popover for="device-editor-info" style="--max-width: 80ww;">
								<p>${_renderBBCode(this.device_data?.device_rendered_description ?? "Invalid device...")}</p>
							</wa-popover>
							<wa-button appearance="plain" id="device-editor-info" ?disabled=${!this.device_data}>
								<wa-icon name="circle-info" variant="solid" label="Info"></wa-icon>
							</wa-button>
							<wa-button appearance="plain"
								@click=${() => this._resetDeviceData(null)}
							>
								<wa-icon name="rotate-left" variant="solid" label="Reset"
									style="color: ${this.device_data_is_original ? "" : "var(--wa-color-orange)"};"
								></wa-icon>
							</wa-button>
							<wa-button appearance="plain"
								@click=${() => this.parentElement?.appendChild(this._cloneDevice())}
							>
								<wa-icon name="clone" variant="solid" label="Clone"></wa-icon>
							</wa-button>
							<wa-button appearance="plain"
								@click=${this.remove}
							>
								<wa-icon name="trash" variant="solid" label="Remove" style="color: var(--wa-color-red);"></wa-icon>
							</wa-button>
							<wa-button-group orientation="vertical" style="vertical-align: middle;">
								<wa-button appearance="plain" size="xs" class="tiny-button" .disabled=${this.previousElementSibling ? false : true}
									@click=${() => { if (this.previousElementSibling) {
										(this.previousElementSibling as LitElement)?.requestUpdate();
										this.parentElement?.insertBefore(this, this.previousElementSibling);
										this.requestUpdate();
									} }}
								>
									<wa-icon name="chevron-up" variant="solid" label="Move Up"></wa-icon>
								</wa-button>
								<wa-button appearance="plain" size="xs" class="tiny-button" .disabled=${this.nextElementSibling ? false : true}
									@click=${() => { if (this.nextElementSibling) {
										(this.nextElementSibling as LitElement)?.requestUpdate();
										this.parentElement?.insertBefore(this.nextElementSibling, this);
										this.requestUpdate();
									} }}
								>
									<wa-icon name="chevron-down" variant="solid" label="Move Down"></wa-icon>
								</wa-button>
							</wa-button-group>
						</div>
					</div>
					
					<img src="${window.SiteConfig!.baseUrl}/tni-docs/assets/devices/${this.device_data?.product_name.replaceAll(/[\.\/\\]/g, "")}.webp" class="header-device-img" />
				</div>
				${body}
			</wa-card>
		`;
	}
	
	private _generateTemplatesForPeripherals(logic_controller: TniJsonDeviceLogicController, custom_data: CustomDeviceData): TemplateResult[] {
		custom_data.peripherals ??= {};
		const templates: TemplateResult[] = [];
		for (let port_index = 0; port_index < logic_controller.ports.length; port_index++) {
			const port = logic_controller.ports[port_index]!;
			if (port.type != TniSocketType.SATA35_SLOT) continue;

			templates.push(html`
				<my-combobox
					without_clear without_filtering without_chevron placeholder="0" style="width: 40px; --wa-form-control-padding-inline: 5px;"
					value=${custom_data.peripherals[port_index] ?? ""}
					@my-value-confirm=${(e: CustomEvent<MyCombobox>) => {
						!e.detail.value ? delete custom_data.peripherals![port_index] : custom_data.peripherals![port_index] = e.detail.value;
						this.requestUpdate();
					}}
				>
					<wa-dropdown-item value="">
						<span class="my-combobox-ignore">Empty</span>
					</wa-dropdown-item>
					${this._dropdown_items_satas_templates}
				</my-combobox>
			`);
		}
		return templates;
	}
	private _getSataPorts(logic_controller: TniJsonDeviceLogicController): number[] {
		const sata_port_indices = [];
		for (let port_index = 0; port_index < logic_controller.ports.length; port_index++) {
			const port = logic_controller.ports[port_index]!;
			if (port.type != TniSocketType.SATA35_SLOT) continue;
			sata_port_indices.push(port_index);
		}
		return sata_port_indices;
	}

	private _generateTemplatesForProgramDropdownItems(excess_cpu: number, excess_mem: number, excess_sto: number, excess_sto_max: number): TemplateResult<1>[] {
		const templates = [];
		if (this._data) {
			for (const program_id in this._data.programs) {
				const program = this._data.programs[program_id]!;
				if (program.release_name.length > 0) {
					const program_size = program.code_size + program.data_size;
					const fits_cpu_mem = program.cpu_load <= excess_cpu && program.stack_size <= excess_mem;
					const color = fits_cpu_mem && program_size <= excess_sto
						? ""
						: fits_cpu_mem && program_size <= excess_sto_max
						? "var(--wa-color-yellow-90)"
						: "var(--wa-color-red-80)";
					templates.push(html`
						<wa-dropdown-item value=${program_id} style="color: ${color};">
							${program.release_name}
							<span slot="details">${program.cpu_load}/${program.stack_size}/${program.code_size+program.data_size}</span>
						</wa-dropdown-item>
					`)
				}
			}
		}
		return templates;
	}

	private _generateTemplatesForPrograms(programs: TniJsonProgramId[], excess_cpu: number, excess_mem: number, excess_sto: number, excess_sto_max: number): TemplateResult[] {
		if (!this._data) return [];

		const bad_combination_programs: Record<number, (string|TemplateResult)[]> = {};
		for (const combination of BAD_PROGRAM_COMBINATIONS) {
			let first_found_indices = new Set<number>();
			let found_indices = new Set<number>();
			for (const test of combination[0]) {
				let found = false;
				for (let i = 0; i < programs.length; i++) {
					const program_id = programs[i]!;
					const program = this._data.programs[program_id];
					// @ts-ignore: I can not be bothered to appease typescript here...
					if (program && (program.release_name == test || program.modifiers.includes(test))) {
						if (!found && !first_found_indices.has(i)) {
							first_found_indices.add(i);
							found = true;
						}
						found_indices.add(i);
					}
				}
				if (!found) {
					found_indices.clear();
					break;
				}
			}
			for (const index of found_indices) {
				if (!bad_combination_programs[index]) bad_combination_programs[index] = [];
				bad_combination_programs[index]!.push(html`Combination of; ${combination[0].join(", ")}<br>${combination[1]}`);
			}
		}

		const templates: TemplateResult[] = [];
		for (let i = 0; i < programs.length; i++) {
			const program_id = programs[i]!;
			const program = this._data.programs[program_id];
			const program_size = (program?.code_size ?? 0) + (program?.data_size ?? 0);

			const warnings = [];
			if (bad_combination_programs[i]) {
				for (const msg of bad_combination_programs[i]!) {
					warnings.push(msg);
				}
			}

			if (warnings.length > 0) {
				warnings.push(html`<span style="color: gray;">These warnings are pending proper verification.<br>Please contact @Avril112113 for amendments and additions!</span>`);
			}

			let tooltipRef: WaTooltip|null = null;
			let tooltipAnchorRef: WaIcon|null = null;
			let closeTimeout: number;
			const openTooltip = () => {
				clearTimeout(closeTimeout);
				if (tooltipRef && tooltipAnchorRef) {
					tooltipRef.anchor = tooltipAnchorRef;
					tooltipRef.open = true;
				}
			};
			const closeTooltip = () => {
				clearTimeout(closeTimeout);
				closeTimeout = window.setTimeout(() => {
					if (tooltipRef) tooltipRef.open = false;
				}, 150);
			};

			templates.push(html`
				<tr>
					<th>
						<wa-tooltip
							${ref((el) => tooltipRef = el as WaTooltip)}
							trigger="manual"
							@mouseenter=${openTooltip}
							@mouseleave=${closeTooltip}
						>
							${join(warnings, html`<br><br>`)}
						</wa-tooltip>
						<my-combobox value=${program_id}
							@my-value-confirm=${(e: CustomEvent<MyCombobox>) => { programs[i] = e.detail.value ?? ""; this.requestUpdate(); }}
						>
							${warnings.length <= 0
								? html`<wa-icon slot="start" name="code"></wa-icon>`
								: html`
									<wa-icon ${ref((el) => tooltipAnchorRef = el as WaIcon)} slot="start" name="triangle-exclamation" style="color: yellow;"
										@mouseenter=${openTooltip}
										@mouseleave=${closeTooltip}
									></wa-icon>
								`
							}
							
							${cache(this._generateTemplatesForProgramDropdownItems(
								excess_cpu + (program?.cpu_load ?? 0),
								excess_mem + (program?.stack_size ?? 0),
								excess_sto + program_size, excess_sto_max + program_size
							))}
						</my-combobox>
					</th>
					<td style="white-space: nowrap; text-align: center;"><b>CPU:</b><br>${program?.cpu_load ?? "?"}</td>
					<td style="white-space: nowrap; text-align: center;"><b>Mem:</b><br>${program?.stack_size ?? "?"}</td>
					<td style="white-space: nowrap; text-align: center;"><b>Size:</b><br>${!program ? "?" : _renderProgramSize(program)}</td>
					<td style="white-space: nowrap;">
						<wa-popover for="device-editor-program-info-${i}" style="--max-width: 80ww;">
							<p>${_renderBBCode(program?.rendered_description ?? "Invalid program...")}</p>
						</wa-popover>
						<wa-button appearance="plain" id="device-editor-program-info-${i}">
							<wa-icon name="circle-info" variant="solid" label="Info"></wa-icon>
						</wa-button>
						<wa-button appearance="plain"
							@click=${() => { programs.splice(i, 1); this.requestUpdate(); }}
						>
							<wa-icon name="trash" variant="solid" label="Remove" style="color: var(--wa-color-red);"></wa-icon>
						</wa-button>
						<wa-button-group orientation="vertical" style="vertical-align: middle;">
							<wa-button appearance="plain" size="xs" class="tiny-button" .disabled=${i <= 0}
								@click=${() => { if (i > 0) {
									[programs[i], programs[i-1]] = [programs[i-1]!, programs[i]!];
									this.requestUpdate();
								} }}
							>
								<wa-icon name="chevron-up" variant="solid" label="Move Up"></wa-icon>
							</wa-button>
							<wa-button appearance="plain" size="xs" class="tiny-button" .disabled=${i >= programs.length-1}
								@click=${() => { if (i < programs.length) {
									[programs[i], programs[i+1]] = [programs[i+1]!, programs[i]!];
									this.requestUpdate();
								} }}
							>
								<wa-icon name="chevron-down" variant="solid" label="Move Down"></wa-icon>
							</wa-button>
						</wa-button-group>
					</td>
				</tr>
			`);
		}
		return templates;
	}

	private _generateTemplatesForUseStack(programs: TniJsonProgramId[], used_mem: number, used_sto: number) {
		if (!this._data) return [];

		const installed_cpu = this.device_data?.logic_controller?.installed_cpu ?? 0;
		const installed_mem = this.device_data?.logic_controller?.installed_mem ?? 0;
		const installed_sto = this.device_data?.logic_controller?.installed_sto ?? 0;
		const free_mem = installed_mem - used_mem;
		const free_sto = installed_sto - used_sto;

		const templates: TemplateResult[] = [];
		const addConsumeTemplate = (use_config_id: TniJsonUseConfigId, consume: number, policy: TniTraversalConsumptionPolicy) => {
			const use_config = this._data!.use_configs[use_config_id];
			if (!use_config) {
				console.warn("Encountered missing use_config:", use_config_id);
				return;
			}
			const policy_name = 
				consume <= 1 ? ""
				: policy == TniTraversalConsumptionPolicy.ALL_OR_NOTHING ? "all or nothing"
				: policy == TniTraversalConsumptionPolicy.FRAGMENTED_USE_ALLOWED ? "fragmented allowed"
				: policy;
			let better_descript = use_config.constraint_descript;
			if (better_descript.startsWith("(") && better_descript.endsWith(")") && (better_descript.match(/[()]/g) || []).length == 2) {
				better_descript = better_descript.substring(1, better_descript.length-2);
			}
			templates.push(html`
				<tr style="color: var(--wa-color-red-80);">
					<td style="float: right;"><code><b>[${-consume}]</b></code></td>
					<td><code>
						<b>${better_descript}</b>
						${policy_name.length > 0 ? html`&nbsp;<small><i>(${policy_name})</i></small>` : ""}
					</code></td>
				</tr>
			`)
		};
		const addProduceTemplate = (use_config_id: TniJsonUseConfigId, produce: number, limit_type: TniProduceLimitType, limit_factor: number, produce_factor: number) => {
			const use_config = this._data!.use_configs[use_config_id];
			if (!use_config) {
				console.warn("Encountered missing use_config:", use_config_id);
				return;
			}
			let limit: string;
			let limit_mul_part = limit_factor != 0 ? `*${limit_factor}` : "";
			switch (limit_type) {
				case TniProduceLimitType.LIMITED_BY_LIMIT_FACTOR:
					limit = String(limit_factor);
					break;
				case TniProduceLimitType.LIMITED_BY_PRODUCE_FACTOR:
					limit = String(produce_factor);
					break;
				case TniProduceLimitType.LIMITED_BY_TARGET_TOTAL_CPU:
					limit = `${installed_cpu * limit_factor} (cpu${limit_mul_part})`;
					break;
				case TniProduceLimitType.LIMITED_BY_TARGET_TOTAL_MEM:
					limit = `${installed_mem * limit_factor} (mem${limit_mul_part})`;
					break;
				case TniProduceLimitType.LIMITED_BY_TARGET_TOTAL_STORAGE:
					limit = `${installed_sto * limit_factor} (sto${limit_mul_part})`;
					break;
				case TniProduceLimitType.LIMITED_BY_TARGET_FREE_MEMORY:
					limit = `${free_mem * limit_factor} (free mem${limit_mul_part})`;
					break;
				default:
					limit = `?${limit_mul_part}`;
					break;
			}

			templates.push(html`
				<tr style="color: var(--wa-color-green-95);">
					<td style="float: right;"><b><code>[${produce}/${limit}]</code></b></td>
					<td><b><code>${use_config.use_value}</code></b></td>
				</tr>
			`)
		};
		for (let i = 0; i < programs.length; i++) {
			const program_id = programs[i]!;
			const program = this._data.programs[program_id];
			if (!program) {
				if (program_id.length > 0)
					console.warn("Invalid program_id:", program_id);
				continue;
			}

			if (program.AlwaysProduce) {
				addProduceTemplate(program.AlwaysProduce.produce_use_config, program.AlwaysProduce.produce_factor, program.AlwaysProduce.produce_limit_type, program.AlwaysProduce.limit_factor, program.AlwaysProduce.produce_factor);
			}
			if (program.TraversalConsume) {
				addConsumeTemplate(program.TraversalConsume.consume_use_config, program.TraversalConsume.consume_factor, program.TraversalConsume.consumption_policy);
				// will_produce
				if (program.TraversalConsume.produce_use_config) {
					addProduceTemplate(program.TraversalConsume.produce_use_config, program.TraversalConsume.produce_factor, program.TraversalConsume.produce_limit_type, program.TraversalConsume.limit_factor, program.TraversalConsume.produce_factor);
				}
			}
			if (program.UseStorage) {
				const use_config = this._data!.use_configs[program.UseStorage.stored_use_config]!;
				templates.push(html`
					<tr style="color: var(--wa-color-green-95);">
						<td style="float: right;"><b><code>[0/+${Math.max(0, program.UseStorage.storage_factor * free_sto)}]</code></b></td>
						<td><b><code>${use_config.use_value}</code></b></td>
					</tr>
				`)
			}
		}
		return templates;
	}

	override willUpdate(changedProperties: Map<PropertyKey, unknown>) {
		super.willUpdate(changedProperties);

		if (changedProperties.has("_data")) {
			this.__recalc_satas();
		}

		const old_device_id = changedProperties.get("device_id") as TniJsonDeviceId ?? this.device_id;
		if (changedProperties.has('device_id')) {
			if (this.device_id && this.device_id.startsWith("units/")) {
				this.device_id = "standardized_" + this.device_id;
			}
			if (this._data && this.device_id) {
				const device = this.device_data_original;
				if (!device) {
					const entry = Object.entries(this._data.devices).find(([key, val]) => val.product_name === this.device_id);
					if (entry) {
						this.device_id = entry[0];
					} else {
						this.device_id = null;
					}
				}
			}
		}
		if (!this.device_data || this.device_id != old_device_id)
			this._resetDeviceData(old_device_id);
	}

	override updated(changedProperties: PropertyValues): void {
		super.updated(changedProperties);

		this.dispatchEvent(new Event('device-editor-updated', {bubbles: true, composed: true}));
		EditorConfig.delta_san = EditorConfig.delta_san;
	}

	override disconnectedCallback(): void {
		EditorConfig.delta_san = EditorConfig.delta_san;
	}

	// `previousDeviceId` should be null to enforce a full reset.
	private _resetDeviceData(previousDeviceId: string|null) {
		const data_original_prev = this._data && previousDeviceId ? this._data.devices[previousDeviceId] ?? null : null;
		const data_original = this.device_data_original;
		if (data_original) {
			const old_data = this.device_data;
			this.device_data = structuredClone(data_original);
			this.device_data.CustomData = { };

			if (old_data && old_data.logic_controller && data_original.logic_controller && this.custom_data?.sata_combination) {
				let sata_combination = this.custom_data.sata_combination;
				const sata_ports = this._getSataPorts(data_original.logic_controller)
				if (sata_ports.length > 0) {
					this.device_data.CustomData.peripherals = {};
					for (let i = 0; i < sata_combination.plug_ids.length; i++) {
						const plug_id = sata_combination.plug_ids[i]!;
						this.device_data.CustomData.peripherals[sata_ports[i]!] = plug_id;
					}
				}
			}
			this.custom_data = undefined;

			if (this.device_data_partial) {
				_.merge(this.device_data, this.device_data_partial);
				this.device_data_partial = null;
			}

			if (previousDeviceId && this.device_data && old_data && data_original_prev) {
				if (this.device_data.logic_controller && old_data.logic_controller && data_original_prev.logic_controller) {
					if (!_.isEqual(old_data.logic_controller.installed_programs, data_original_prev.logic_controller.installed_programs)) {
						this.device_data.logic_controller.installed_programs = old_data.logic_controller.installed_programs;
					}
				}
			}
		} else {
			this.device_data = null;
		}
		this.requestUpdate("device_data");
	}

	private _cloneDevice() {
		const clone = this.cloneNode() as DeviceEditor;
		clone.device_data = structuredClone(this.device_data);
		return clone;
	}

	serialize() {
		return {
			device_id: this.device_id,
			// device_data: this.device_data_is_original ? null : this.device_data,
			device_data_diff: this.device_data && this.device_data_original && !this.device_data_is_original
				? _getDeepDiff(this.device_data, this.device_data_original)
				: null,
		};
	}
	deserialize(data: ReturnType<typeof this.serialize>) {
		console.debug("MyDevice.deserialize", data);
		this.device_id = typeof data.device_id === "string" ? data.device_id : null;
		// this.device_data = typeof data.device_data == "object" ? data.device_data : null;
		if (typeof data.device_data_diff === "object") {
			this.device_data_partial = data.device_data_diff;
		}
		this.requestUpdate();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"device-editor": DeviceEditor;
	}
	interface Window {
		"getTotalSanStorageDelta": typeof getTotalSanStorageDelta;
	}
}
